import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export function formatDateTime(date: string | Date): string {
  return `${formatDate(date)}, ${formatTime(date)}`;
}

// ─── Currency Formatting ─────────────────────────────────

const CURRENCY_CONFIG: Record<string, { locale: string; symbol: string }> = {
  ZAR: { locale: 'en-ZA', symbol: 'R' },
  USD: { locale: 'en-US', symbol: '$' },
  EUR: { locale: 'de-DE', symbol: '€' },
  GBP: { locale: 'en-GB', symbol: '£' },
  XAF: { locale: 'fr-CM', symbol: 'FCFA' },
  XOF: { locale: 'fr-SN', symbol: 'CFA' },
  NGN: { locale: 'en-NG', symbol: '₦' },
  KES: { locale: 'en-KE', symbol: 'KSh' },
  GHS: { locale: 'en-GH', symbol: 'GH₵' },
};

/**
 * Format a monetary amount using the organization's currency.
 * Falls back to ZAR if the currency code is unknown.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currencyCode = 'ZAR',
): string {
  if (amount == null) amount = 0;
  const cfg = CURRENCY_CONFIG[currencyCode] ?? CURRENCY_CONFIG.ZAR;
  const formatted = amount.toLocaleString(cfg.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${cfg.symbol} ${formatted}`;
}

/**
 * Format cents to a currency display string.
 */
export function formatCurrencyCents(
  cents: number | null | undefined,
  currencyCode = 'ZAR',
): string {
  return formatCurrency((cents ?? 0) / 100, currencyCode);
}

/** Supported currency codes for the currency selector. */
export const SUPPORTED_CURRENCIES = [
  { value: 'ZAR', label: 'ZAR — South African Rand (R)' },
  { value: 'USD', label: 'USD — US Dollar ($)' },
  { value: 'EUR', label: 'EUR — Euro (€)' },
  { value: 'GBP', label: 'GBP — British Pound (£)' },
  { value: 'XAF', label: 'XAF — Central African CFA (FCFA)' },
  { value: 'XOF', label: 'XOF — West African CFA (CFA)' },
  { value: 'NGN', label: 'NGN — Nigerian Naira (₦)' },
  { value: 'KES', label: 'KES — Kenyan Shilling (KSh)' },
  { value: 'GHS', label: 'GHS — Ghanaian Cedi (GH₵)' },
];
